import React from 'react';
import {observable, action, computed, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/scss';
import 'swiper/scss/scrollbar';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';
import 'swiper/scss/zoom';
import {Keyboard, Scrollbar, Navigation, Zoom} from 'swiper/modules';
import {r} from '../../rendering/core';
import {prerender_set_pcs} from '../../rendering/web-only-render';
import * as emitter from '../../../global/emitter';
import AbTestStore, {ABTestKeys} from '../../../global/store/ab-test.store';
import SliderTHModal from './slider-th-modal';
import {ProductCustomizationState} from '../../rendering/render.def';
import {ariaPulse, isNullOrWhiteSpace, modal, Portal, RenderType} from '../../../util';
import {StandardSlide} from './standard-slide';

declare var __AB_Insignia_Jan26: number;

@observer
export default class SliderTH extends React.Component {
    @observable zoom_id: string = '';
    @observable slideCount: number = 6;
    @observable visibleSlidesCount: number = 0;

    constructor(props) {
        super(props);
        this.loadSwiper = this.loadSwiper.bind(this);
        this.loadSwiperZoom = this.loadSwiperZoom.bind(this);
        this.toggleZoom = this.toggleZoom.bind(this);
        makeObservable(this);
        this.getSlidesPerView();

        document.addEventListener('modal-closed', function (e: any) {
            if (e.detail === 'modal-prod-swiper-th') {
                s.ui.zoom_thumbnails = false;
            }
        });
    }

    abForceUpdate = async () => this.forceUpdate();

    componentDidMount() {
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', this.abForceUpdate);
    }

    componentWillUnmount() {
        emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'ab');
    }

    @computed get show_slider(): boolean {
        if (!s.ready || !s.prod) {
            return false;
        }

        return !(s.prod.MouldingID < 1 && s.prod.Mat1_ID < 1 && s.prod.th1Disabled && s.prod.th2Disabled && s.prod.th3Disabled && s.prod.th4Disabled && s.prod.th5Disabled && s.prod.th6Disabled && s.prod.th7Disabled && s.prod.th8Disabled && s.prod.th9Disabled && s.prod.th10Disabled);
    }

    @computed get do_loop(): boolean {
        if (!s.ready) {
            return true;
        }

        if (s.prod.CustomizationMethod !== 1) {
            this.visibleSlidesCount++;
        }

        if (s.ps.moulding_id > 0 && s.ui.moulding_has_zoom) {
            this.visibleSlidesCount++;
        }

        if (s.ps.mat1_id > 0) {
            this.visibleSlidesCount++;
        }

        if (s.ps.insignia_id > 0) {
            this.visibleSlidesCount++;
        }

        if (s.prod.real_images_th && s.prod.real_images_th.length > 0) {
            this.visibleSlidesCount += s.prod.real_images_th.length;
        }

        if (!s.prod.disableGlassUpgrades) {
            this.visibleSlidesCount++;
        }

        for (let i = 1; i <= 10; i++) {
            const key = `th${i}`;
            const disabledKey = `${key}Disabled`;

            if (!s.prod[disabledKey] && !isNullOrWhiteSpace(s.prod[key])) {
                this.visibleSlidesCount++;
            }
        }

        if (this.visibleSlidesCount < this.slideCount) {
            return false;
        }

        return s.prod.CustomizationMethod >= 2;
    }

    @computed get show_navigation(): boolean {
        if (!s.ready) {
            return false;
        }

        return this.visibleSlidesCount >= this.slideCount;
    }

    @action getSlidesPerView(value: number = 0) {
        if (window.innerWidth < 340) {
            this.slideCount = 3;
        } else if (window.innerWidth < 400) {
            this.slideCount = 3 + value;
        } else if (window.innerWidth < 510) {
            this.slideCount = 4 + value;
        } else if (window.innerWidth < 580) {
            this.slideCount = 5 + value;
        } else {
            this.slideCount = 6 + value;
        }
    }

    @action
    loadSwiper = async (swiper: any) => {
        ariaPulse();
    };

    @action
    loadSwiperZoom = async (swiper: any) => {
        if (!isNullOrWhiteSpace(this.zoom_id)) {
            const ele = document.getElementById(this.zoom_id);
            const index = parseInt(ele?.attributes['data-swiper-slide-index'].value, 10);

            swiper.slideToLoop(index);
            setTimeout(function () {
                swiper.slideToLoop(index);
            }, 250);
            setTimeout(function () {
                swiper.slideToLoop(index);
            }, 1250);
        }

        this.zoom_id = '';
        ariaPulse();
    };

    @action
    toggleZoom = async (e: any) => {
        this.zoom_id = e;
        prerender_set_pcs();

        let clone: ProductCustomizationState;

        try {
            clone = structuredClone(s.pcs);
        } catch (e) {
            clone = JSON.parse(JSON.stringify(s.pcs));
        }

        s.ps.css_zoom = r.render(clone, RenderType.Zoom);
        s.ui.zoom_thumbnails = true;
        modal('modal-prod-swiper-th');
    };

    @computed get hide_dynamic(): boolean {
        if (!s.ready) {
            return false;
        }

        return [70214, 70215, 70244, 84054].includes(s.prod.ProductID) || ['391332'].includes(s.prod.ItemID);
    }

    render() {
        if (!s.ready) return;

        if (!this.show_slider) return;

        const portalAllowsAutoSlides = Portal?.PortalID !== 3;
        const showAutoSlide1 = portalAllowsAutoSlides && !isNullOrWhiteSpace(s.prod.th_auto_2_1);
        const isCustom = s.prod.CustomizationMethod > 1;
        const mat2 = s.ps.mat2_id > 0 ? s.ps.mat2_id : 0;
        const insignia = s.ps.insignia_id > 0 ? s.ps.insignia_id : 0;
        const lino = s.ps.lino_id > 0 ? s.ps.lino_id : 0;
        const insigniaABOld = typeof __AB_Insignia_Jan26 !== 'undefined' && __AB_Insignia_Jan26 === 1;
        const thumbnails = [
            {key: 'th1', index: 1},
            {key: 'th2', index: 2},
            {key: 'th3', index: 3},
            {key: 'th4', index: 4},
            {key: 'th5', index: 5},
            {key: 'th7', index: 7},
            {key: 'th8', index: 8},
            {key: 'th9', index: 9},
            {
                key: 'th10',
                index: 10,
                condition: s.prod.CustomizationMethod === 2,
            },
        ];

        const slideProps = (id: string, desc: string) => {
            return {
                onClick: () => this.toggleZoom(id),
                onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.toggleZoom(id);
                    }
                },
                tabIndex: 0,
                role: 'button',
                'aria-label': `Open larger image of ${desc}`,
            };
        };

        return (
            <div>
                <Swiper
                    id="prod-swiper-th"
                    onInit={this.loadSwiper}
                    slidesPerView={this.slideCount}
                    centeredSlidesBounds={!this.do_loop}
                    centeredSlides={!this.do_loop}
                    centerInsufficientSlides={!this.do_loop}
                    grabCursor={this.do_loop}
                    zoom={false}
                    keyboard={{
                        enabled: this.do_loop,
                    }}
                    scrollbar={false}
                    navigation={this.show_navigation}
                    height={75}
                    loop={this.do_loop}
                    pagination={false}
                    modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                    aria-roledescription="carousel"
                    role="region"
                    aria-label="Product images"
                >
                    {s.prod.CustomizationMethod === 2 && (
                        <SwiperSlide {...slideProps('slide-prod', 'product')}>
                            <StandardSlide
                                url={
                                    !isCustom
                                        ? s.prod.Image_Thumb
                                        : s.ps.is_caf
                                          ? `https://${Portal?.IMGDomain}/i/0_220_250_${s.prod.ProductID}_${s.ps.moulding_id}_${s.ps.mat1_id}_${mat2}_${insignia}_${s.prod.DocumentID}_${s.prod.ExtraEntityID}_0___0____0__11.000_0_8.500_0_0_0_0.png`
                                          : `https://${Portal?.IMGDomain}/i/${s.prod.ProductID}_220_250.png?MouldingID=${s.ps.moulding_id}&PrimaryMatID=${s.ps.mat1_id}&SecondaryMatID=${mat2}&InsigniaID=${insignia}&LinoID=${lino}`
                                }
                                alt={'Product'}
                                border={true}
                            />
                        </SwiperSlide>
                    )}

                    {Portal?.PortalID !== 3 && s.ps.moulding_id > 0 && s.ui.moulding_has_zoom && (
                        <SwiperSlide {...slideProps('slide-prod-moulding', s.ui.moulding_th5 ? 'corner view of moulding' : 'moulding')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/entities/moulding/${s.ui.moulding_th5 ? `${s.ps.moulding_id}_th5_75.png` : s.ui.moulding_zoom_path_th}`}
                                alt={'Moulding'}
                            />
                        </SwiperSlide>
                    )}

                    {Portal?.PortalID !== 3 && s.ps.moulding_id > 0 && s.ui.moulding_th6 && (
                        <SwiperSlide {...slideProps('slide-prod-moulding-2', 'profile view of moulding')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/entities/moulding/${s.ps.moulding_id}_th6_75.png`}
                                alt={'Moulding Profile'}
                            />
                        </SwiperSlide>
                    )}

                    {!insigniaABOld && s.ps.insignia_id > 0 && (
                        <SwiperSlide {...slideProps('slide-prod-ins', 'insignia')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/entities/insignia/v2/${s.ui.name_insignia.toLowerCase()}_90.png`}
                                alt={s.ui.title_insignia}
                                backgroundImage={`image-set(url('https://${Portal?.CDNDomain}/images/entities/primarymat/v2/${s.ui.name_mat1}_320.webp') type('image/webp') 1x, url('https://${Portal?.CDNDomain}/images/entities/primarymat/v2/${s.ui.name_mat1}_320.jpg') type('image/jpeg') 1x)`}
                            />
                        </SwiperSlide>
                    )}

                    {insigniaABOld && s.prod.CustomizationMethod !== 2 && s.prod.OldInsigniaThumb && s.prod.OldInsigniaThumb != '' && (
                        <SwiperSlide {...slideProps('slide-prod-old-ins', 'insignia')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/products/insignia/thumb/${s.prod.OldInsigniaThumb.toLowerCase()}`}
                                alt={s.ui.title_insignia}
                            />
                        </SwiperSlide>
                    )}

                    {s.ps.lino_id > 0 && (
                        <SwiperSlide {...slideProps('slide-prod-lino', 'lino')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/entities/linotype_product/v2/${s.ps.lino_base_name.replace(/ /g, '_')}_150.png`.replace('occupation_', '').toLowerCase()}
                                backgroundImage={`image-set(url('https://${Portal?.CDNDomain}/images/entities/primarymat/v2/${s.ui.name_mat1}_320.webp') type('image/webp') 1x, url('https://${Portal?.CDNDomain}/images/entities/primarymat/v2/${s.ui.name_mat1}_320.jpg') type('image/jpeg') 1x)`}
                                imgStyle={{objectFit: 'contain'}}
                                alt={'Lino'}
                            />
                        </SwiperSlide>
                    )}

                    {s.ps.mat1_id > 0 && (
                        <SwiperSlide {...slideProps('slide-prod-matcombo', 'mats')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/entities/matcombos/75_${s.ps.mat1_id}${s.ps.mat2_id > 0 ? `_${s.ps.mat2_id.toString()}` : ''}.png`}
                                alt={s.ui.title_mat_combo}
                            />
                        </SwiperSlide>
                    )}

                    {!AbTestStore.ABIsVariant(ABTestKeys.product_glass, 1) && s.prod.AllowGlassID2 > 0 && s.prod && s.prod.IsFrame && s.prod.ProductID !== 84054 && s.prod.ProductID !== 70214 && !s.prod.disableGlassUpgrades && (
                        <SwiperSlide {...slideProps('slide-prod-glassoptions', 'glass options')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/cached/general/glass-all-75.png`}
                                alt={'Glass Options'}
                            />
                        </SwiperSlide>
                    )}

                    {showAutoSlide1 && (
                        <SwiperSlide {...slideProps('slide-prod-th_auto_2_1', 'product thumbnail #1')}>
                            <StandardSlide
                                url={s.prod.th_auto_2_1}
                                border={true}
                                alt={'Product thumbnail #1'}
                            />
                        </SwiperSlide>
                    )}

                    {(s.prod.ProductTypeID == 83 || s.prod.ProductTypeID == 84 || s.prod.ProductTypeID == 87) && Portal?.PortalID !== 3 && !isNullOrWhiteSpace(s.prod.th_auto_2_17) && s.prod.th_auto_2_17 && (
                        <SwiperSlide {...slideProps('slide-prod-th_auto_2_17', 'product thumbnail #17')}>
                            <StandardSlide
                                url={s.prod.th_auto_2_17}
                                border={true}
                                alt={'Product thumbnail #17'}
                            />
                        </SwiperSlide>
                    )}

                    {Portal?.PortalID !== 3 && s.prod && s.prod.is_tassel_cord && (
                        <SwiperSlide {...slideProps('slide-prod-tassel-cord', 'tassel cord')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/cached/general/tassel-cord-th.png`}
                                alt={`Tassel & Cord Frame holds ${s.si.ProdInstText} graduation tassel or honor cords`}
                            />
                        </SwiperSlide>
                    )}

                    {(s.prod.ProductTypeID == 83 || s.prod.ProductTypeID == 84 || s.prod.ProductTypeID == 87) && Portal?.PortalID !== 3 && !isNullOrWhiteSpace(s.prod.th_auto_2_16) && s.prod.th_auto_2_16 && (
                        <SwiperSlide {...slideProps('slide-prod-th_auto_2_16', 'product thumbnail #16')}>
                            <StandardSlide
                                url={s.prod.th_auto_2_16}
                                border={true}
                                alt={'Product thumbnail #16'}
                            />
                        </SwiperSlide>
                    )}

                    {thumbnails.map(({key, index, condition = true}) => {
                        const value = s.prod[key];
                        const disabled = s.prod[`${key}Disabled`];

                        if (disabled || isNullOrWhiteSpace(value) || !condition) {
                            return null;
                        }

                        return (
                            <SwiperSlide
                                key={key}
                                {...slideProps(`slide-prod-${key}`, `product thumbnail #${index}`)}
                            >
                                <StandardSlide
                                    border
                                    url={`https://${Portal?.CDNDomain}${value}_75.jpg`}
                                    alt={`Product thumbnail #${index}`}
                                />
                            </SwiperSlide>
                        );
                    })}

                    {s.prod.real_images_th.map(m => (
                        <SwiperSlide {...slideProps(`slide-prod-real-${m.id}`, 'this real life image')}>
                            <StandardSlide
                                url={`https://${Portal?.CDNDomain}/images/cached/rp/rp${m.id}_th250.jpg`}
                                alt={'Real Life Image'}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
                <SliderTHModal
                    onSwiperInit={this.loadSwiperZoom}
                    onImageClick={this.toggleZoom}
                />
            </div>
        );
    }
}
